import { MGPFallible, MGPOptional, MGPValidation, Utils } from '@everyboard/lib';
import { GameNode } from '../../jscaip/AI/GameNode';
import { FourStatePiece } from '../../jscaip/FourStatePiece';
import { GameStatus } from '../../jscaip/GameStatus';
import { GipfCapture } from '../../jscaip/GipfProjectHelper';
import { HexaDirection } from '../../jscaip/HexaDirection';
import { FlatHexaOrientation } from '../../jscaip/HexaOrientation';
import { Player } from '../../jscaip/Player';
import { PlayerNumberMap } from '../../jscaip/PlayerMap';
import { Rules } from '../../jscaip/Rules';
import { GipfFailure } from './GipfFailure';
import { GipfPlacement } from './GipfMove';
import { GipfState } from './GipfState';
export class GipfNode extends GameNode {
}
export class GipfRules extends Rules {
    static singleton = MGPOptional.empty();
    static get() {
        if (GipfRules.singleton.isAbsent()) {
            GipfRules.singleton = MGPOptional.of(new GipfRules());
        }
        return GipfRules.singleton.get();
    }
    getInitialState() {
        const _ = FourStatePiece.EMPTY;
        const N = FourStatePiece.UNREACHABLE;
        const O = FourStatePiece.ZERO;
        const X = FourStatePiece.ONE;
        const board = [
            [N, N, N, X, _, _, O],
            [N, N, _, _, _, _, _],
            [N, _, _, _, _, _, _],
            [O, _, _, _, _, _, X],
            [_, _, _, _, _, _, N],
            [_, _, _, _, _, N, N],
            [X, _, _, O, N, N, N],
        ];
        return new GipfState(board, 0, PlayerNumberMap.of(12, 12), PlayerNumberMap.of(0, 0));
    }
    applyLegalMove(_move, _state, _config, computedState) {
        return new GipfState(computedState.board, computedState.turn + 1, computedState.sidePieces, computedState.capturedPieces);
    }
    static applyCaptures(captures, state) {
        let computedState = state;
        captures.forEach((capture) => {
            computedState = GipfRules.applyCapture(capture, computedState);
        });
        return computedState;
    }
    static applyCapture(capture, state) {
        const player = state.getCurrentPlayer();
        let newState = state;
        const sidePieces = state.sidePieces.getCopy();
        const capturedPieces = state.capturedPieces.getCopy();
        capture.forEach((coord) => {
            const piece = state.getPieceAt(coord);
            newState = newState.setAt(coord, FourStatePiece.EMPTY);
            if (piece.is(player)) {
                sidePieces.add(player, 1);
            }
            else {
                capturedPieces.add(player, 1);
            }
        });
        return new GipfState(newState.board, state.turn, sidePieces, capturedPieces);
    }
    static getPlacements(state) {
        const placements = [];
        FlatHexaOrientation.INSTANCE.getAllBorders(state).forEach((entrance) => {
            if (state.getPieceAt(entrance) === FourStatePiece.EMPTY) {
                placements.push(new GipfPlacement(entrance, MGPOptional.empty()));
            }
            else {
                GipfRules.getAllDirectionsForEntrance(state, entrance).forEach((dir) => {
                    if (GipfRules.isLineComplete(state, entrance, dir) === false) {
                        placements.push(new GipfPlacement(entrance, MGPOptional.of(dir)));
                    }
                });
            }
        });
        return placements;
    }
    static isLineComplete(state, start, dir) {
        return GipfRules.nextGapInLine(state, start, dir).isAbsent();
    }
    static nextGapInLine(state, start, dir) {
        for (let cur = start; state.isOnBoard(cur); cur = cur.getNext(dir)) {
            if (state.getPieceAt(cur) === FourStatePiece.EMPTY) {
                return MGPOptional.of(cur);
            }
        }
        return MGPOptional.empty();
    }
    static applyPlacement(placement, state) {
        const player = state.getCurrentPlayer();
        let newState = state;
        let previousPiece = FourStatePiece.ofPlayer(state.getPreviousOpponent());
        if (placement.direction.isAbsent()) {
            // Only valid if there is an empty spot
            const coord = placement.coord;
            if (state.getPieceAt(coord) !== FourStatePiece.EMPTY) {
                throw new Error('Apply placement called without direction while the coord is occupied');
            }
            newState = newState.setAt(coord, previousPiece);
        }
        else {
            let cur = placement.coord;
            while (newState.isOnBoard(cur) && previousPiece !== FourStatePiece.EMPTY) {
                const curPiece = state.getPieceAt(cur);
                newState = newState.setAt(cur, previousPiece);
                previousPiece = curPiece;
                cur = cur.getNext(placement.direction.get());
            }
        }
        const sidePieces = state.sidePieces.getCopy();
        sidePieces.add(player, -1);
        return new GipfState(newState.board, state.turn, sidePieces, state.capturedPieces);
    }
    getPiecesMoved(state, initialCaptures, placement) {
        const stateAfterCapture = GipfRules.applyCaptures(initialCaptures, state);
        if (placement.direction.isAbsent()) {
            return [placement.coord];
        }
        else {
            const dir = placement.direction.get();
            const moved = [];
            moved.push(placement.coord);
            let cur = placement.coord.getNext(dir);
            while (stateAfterCapture.hasInequalPieceAt(cur, FourStatePiece.EMPTY)) {
                moved.push(cur);
                cur = cur.getNext(dir);
            }
            Utils.assert(stateAfterCapture.hasPieceAt(cur, FourStatePiece.EMPTY), 'getPiecesMoved called with an invalid placement performed on a full line');
            // This is the space filled by the last pushed piece
            moved.push(cur);
            return moved;
        }
    }
    static getAllDirectionsForEntrance(state, entrance) {
        if (FlatHexaOrientation.INSTANCE.isTopLeftCorner(state, entrance)) {
            return [HexaDirection.RIGHT, HexaDirection.DOWN, HexaDirection.UP_RIGHT];
        }
        else if (FlatHexaOrientation.INSTANCE.isTopCorner(state, entrance)) {
            return [HexaDirection.DOWN, HexaDirection.DOWN_LEFT, HexaDirection.RIGHT];
        }
        else if (FlatHexaOrientation.INSTANCE.isTopRightCorner(state, entrance)) {
            return [HexaDirection.DOWN_LEFT, HexaDirection.DOWN, HexaDirection.LEFT];
        }
        else if (FlatHexaOrientation.INSTANCE.isBottomLeftCorner(state, entrance)) {
            return [HexaDirection.UP_RIGHT, HexaDirection.UP, HexaDirection.RIGHT];
        }
        else if (FlatHexaOrientation.INSTANCE.isBottomCorner(state, entrance)) {
            return [HexaDirection.UP, HexaDirection.LEFT, HexaDirection.UP_RIGHT];
        }
        else if (FlatHexaOrientation.INSTANCE.isBottomRightCorner(state, entrance)) {
            return [HexaDirection.LEFT, HexaDirection.UP, HexaDirection.DOWN_LEFT];
        }
        else if (FlatHexaOrientation.INSTANCE.isOnTopLeftBorder(state, entrance)) {
            return [HexaDirection.RIGHT, HexaDirection.DOWN];
        }
        else if (FlatHexaOrientation.INSTANCE.isOnLeftBorder(state, entrance)) {
            return [HexaDirection.UP_RIGHT, HexaDirection.RIGHT];
        }
        else if (FlatHexaOrientation.INSTANCE.isOnBottomLeftBorder(state, entrance)) {
            return [HexaDirection.UP, HexaDirection.UP_RIGHT];
        }
        else if (FlatHexaOrientation.INSTANCE.isOnBottomRightBorder(state, entrance)) {
            return [HexaDirection.LEFT, HexaDirection.UP];
        }
        else if (FlatHexaOrientation.INSTANCE.isOnRightBorder(state, entrance)) {
            return [HexaDirection.LEFT, HexaDirection.DOWN_LEFT];
        }
        else if (FlatHexaOrientation.INSTANCE.isOnTopRightBorder(state, entrance)) {
            return [HexaDirection.DOWN_LEFT, HexaDirection.DOWN];
        }
        else {
            throw new Error('not a border');
        }
    }
    isLegal(move, state) {
        const initialCapturesValidity = this.capturesValidity(state, move.initialCaptures);
        if (initialCapturesValidity.isFailure()) {
            return initialCapturesValidity.toOtherFallible();
        }
        const stateAfterInitialCaptures = GipfRules.applyCaptures(move.initialCaptures, state);
        const noMoreCaptureAfterInitialValidity = this.noMoreCapturesValidity(stateAfterInitialCaptures);
        if (noMoreCaptureAfterInitialValidity.isFailure()) {
            return noMoreCaptureAfterInitialValidity.toOtherFallible();
        }
        const placementValidity = this.placementValidity(stateAfterInitialCaptures, move.placement);
        if (placementValidity.isFailure()) {
            return placementValidity.toOtherFallible();
        }
        const stateAfterPlacement = GipfRules.applyPlacement(move.placement, stateAfterInitialCaptures);
        const finalCapturesValidity = this.capturesValidity(stateAfterPlacement, move.finalCaptures);
        if (finalCapturesValidity.isFailure()) {
            return finalCapturesValidity.toOtherFallible();
        }
        const stateAfterFinalCaptures = GipfRules.applyCaptures(move.finalCaptures, stateAfterPlacement);
        const noMoreCaptureAfterFinalValidity = this.noMoreCapturesValidity(stateAfterFinalCaptures);
        if (noMoreCaptureAfterFinalValidity.isFailure()) {
            return noMoreCaptureAfterFinalValidity.toOtherFallible();
        }
        return MGPFallible.success(stateAfterFinalCaptures);
    }
    capturesValidity(state, captures) {
        let updatedState = state;
        for (const capture of captures) {
            const validity = this.captureValidity(updatedState, capture);
            if (validity.isFailure()) {
                return validity;
            }
            updatedState = GipfRules.applyCapture(capture, updatedState);
        }
        return MGPValidation.SUCCESS;
    }
    captureValidity(state, capture) {
        const player = state.getCurrentPlayer();
        const linePortionOpt = GipfRules.getLinePortionWithFourPiecesOfPlayer(state, player, capture.getLine());
        if (linePortionOpt.isAbsent()) {
            return MGPValidation.failure(GipfFailure.CAPTURE_MUST_BE_ALIGNED());
        }
        const linePortion = linePortionOpt.get();
        const capturable = GipfRules.getCapturable(state, linePortion);
        if (capturable.equals(capture)) {
            return MGPValidation.SUCCESS;
        }
        else {
            return MGPValidation.failure(GipfFailure.INVALID_CAPTURED_PIECES());
        }
    }
    static getLinePortionsWithFourPiecesOfPlayer(state, player) {
        const linePortions = [];
        state.allLines().forEach((line) => {
            const linePortion = GipfRules.getLinePortionWithFourPiecesOfPlayer(state, player, line);
            if (linePortion.isPresent()) {
                linePortions.push(linePortion.get());
            }
        });
        return linePortions;
    }
    static getLinePortionWithFourPiecesOfPlayer(state, player, line) {
        let consecutives = 0;
        const coord = state.getEntranceOnLine(line);
        const dir = line.getDirection();
        let start = coord;
        for (let cur = coord; state.isOnBoard(cur); cur = cur.getNext(dir)) {
            if (state.getPieceAt(cur).is(player)) {
                if (consecutives === 0) {
                    start = cur;
                }
                consecutives += 1;
            }
            else {
                consecutives = 0;
            }
            if (consecutives === 4) {
                return MGPOptional.of({ 0: start, 1: cur, 2: dir });
            }
        }
        return MGPOptional.empty();
    }
    noMoreCapturesValidity(state) {
        const player = state.getCurrentPlayer();
        const linePortions = GipfRules.getLinePortionsWithFourPiecesOfPlayer(state, player);
        if (linePortions.length === 0) {
            return MGPValidation.SUCCESS;
        }
        else {
            return MGPValidation.failure(GipfFailure.MISSING_CAPTURES());
        }
    }
    placementValidity(state, placement) {
        const coordValidity = this.placementCoordValidity(state, placement.coord);
        if (coordValidity.isFailure()) {
            return coordValidity;
        }
        if (state.getPieceAt(placement.coord) !== FourStatePiece.EMPTY) {
            if (placement.direction.isAbsent()) {
                return MGPValidation.failure(GipfFailure.PLACEMENT_WITHOUT_DIRECTION());
            }
            if (GipfRules.isLineComplete(state, placement.coord, placement.direction.get())) {
                return MGPValidation.failure(GipfFailure.PLACEMENT_ON_COMPLETE_LINE());
            }
            for (const dir of GipfRules.getAllDirectionsForEntrance(state, placement.coord)) {
                if (dir === placement.direction.get()) {
                    return MGPValidation.SUCCESS;
                }
            }
            return MGPValidation.failure(GipfFailure.INVALID_PLACEMENT_DIRECTION());
        }
        return MGPValidation.SUCCESS;
    }
    placementCoordValidity(state, coord) {
        if (FlatHexaOrientation.INSTANCE.isOnBorder(state, coord)) {
            return MGPValidation.SUCCESS;
        }
        else {
            return MGPValidation.failure(GipfFailure.PLACEMENT_NOT_ON_BORDER());
        }
    }
    static getCapturable(state, linePortion) {
        // Go into each direction and continue until there are pieces
        const capturable = [];
        const start = linePortion[0];
        const end = linePortion[1];
        const dir = linePortion[2];
        const oppositeDir = dir.getOpposite();
        let cur = start.getNext(oppositeDir);
        while (state.hasInequalPieceAt(cur, FourStatePiece.EMPTY)) {
            // Go backwards to identify capturable pieces before the 4 aligned pieces
            capturable.push(cur);
            cur = cur.getNext(oppositeDir);
        }
        for (let coord = start; coord.equals(end) === false; coord = coord.getNext(dir)) {
            // The 4 pieces are capturable
            capturable.push(coord);
        }
        for (let coord = end; state.hasInequalPieceAt(coord, FourStatePiece.EMPTY); coord = coord.getNext(dir)) {
            // Go forward to identify capturable pieces after the 4 aligned pieces
            capturable.push(coord);
        }
        return new GipfCapture(capturable);
    }
    static getPossibleCaptures(state) {
        const player = state.getCurrentPlayer();
        const captures = [];
        GipfRules.getLinePortionsWithFourPiecesOfPlayer(state, player)
            .forEach((linePortion) => {
            captures.push(GipfRules.getCapturable(state, linePortion));
        });
        return captures;
    }
    static getPlayerScore(state, player) {
        const piecesToPlay = state.getNumberOfPiecesToPlace(player);
        if (piecesToPlay === 0) {
            const captures = GipfRules.getPossibleCaptures(state);
            if (captures.length === 0) {
                // No more pieces to play and no possible capture, player loses
                return MGPOptional.empty();
            }
        }
        const captured = state.getNumberOfPiecesCaptured(player);
        return MGPOptional.of(piecesToPlay + captured * 3);
    }
    static isGameOver(state) {
        const score0 = GipfRules.getPlayerScore(state, Player.ZERO);
        const score1 = GipfRules.getPlayerScore(state, Player.ONE);
        return score0.isAbsent() ||
            score1.isAbsent();
    }
    getGameStatus(node) {
        const state = node.gameState;
        const score0 = GipfRules.getPlayerScore(state, Player.ZERO);
        const score1 = GipfRules.getPlayerScore(state, Player.ONE);
        if (score0.isAbsent()) {
            return GameStatus.ONE_WON;
        }
        else if (score1.isAbsent()) {
            return GameStatus.ZERO_WON;
        }
        else {
            return GameStatus.ONGOING;
        }
    }
}
